(function () {
  // Browser requests must use judge.me with api_token in the query string only.
  // Custom headers (e.g. X-Api-Token) trigger a CORS preflight that Judge.me does not answer.
  const WIDGET_API_BASE = 'https://judge.me/api/v1';

  function parseConfig(sectionEl) {
    const raw = sectionEl.querySelector('[data-showroom-judgeme-config]');
    if (!raw) return null;
    try {
      const config = JSON.parse(raw.textContent);
      config.shopDomain = String(config.shopDomain || '').trim().toLowerCase();
      config.reviewsLimit = Math.max(1, parseInt(config.reviewsLimit, 10) || 16);
      config.apiTokens = [config.apiToken]
        .map((token) => String(token || '').trim())
        .filter(Boolean);
      config.reviewMode = config.reviewMode === 'product' ? 'product' : 'store';
      config.productId = parseInt(config.productId, 10) || 0;
      config.productHandle = String(config.productHandle || '')
        .trim()
        .replace(/^\/+|\/+$/g, '')
        .toLowerCase();
      config.productImage = String(config.productImage || '').trim();
      config.emptyMessage = String(
        config.emptyMessage || 'Be the first to write a review'
      );
      return config;
    } catch {
      return null;
    }
  }

  function meetsMinRating(rating, minRatingKey) {
    const value = Number(rating);
    if (!Number.isFinite(value)) return false;
    if (minRatingKey === '5_only') return value === 5;
    if (minRatingKey === '4_and_above') return value >= 4;
    if (minRatingKey === '3_and_above') return value >= 3;
    return value >= 4;
  }

  function escapeText(text) {
    const el = document.createElement('span');
    el.textContent = text == null ? '' : String(text);
    return el.innerHTML;
  }

  function starsForRating(rating) {
    const count = Math.max(0, Math.min(5, Math.round(Number(rating))));
    return '★'.repeat(count);
  }

  function productIdFromReviewElement(el) {
    const raw =
      el.getAttribute('data-product-id') ||
      el.getAttribute('data-product_id') ||
      el.querySelector('[data-product-id]')?.getAttribute('data-product-id') ||
      '';
    return parseInt(raw, 10) || 0;
  }

  function productHandleFromReviewElement(el) {
    const links = el.querySelectorAll(
      '.jdgm-rev__prod-link, .jdgm-rev__product-link, a[href*="/products/"]'
    );
    for (const link of links) {
      const href = (link.getAttribute('href') || '').split('?')[0];
      const match = href.match(/\/products\/([^/?#]+)/);
      if (match) return decodeURIComponent(match[1]).toLowerCase();
    }
    return '';
  }

  function isReviewForProduct(el, config) {
    if (!config.productId) return false;
    const productId = productIdFromReviewElement(el);
    if (productId) return productId === config.productId;
    const handle = productHandleFromReviewElement(el);
    if (handle && config.productHandle) return handle === config.productHandle;
    return null;
  }

  function filterReviewElements(elements, config, storeReviewsOnly) {
    const list = Array.from(elements);
    if (storeReviewsOnly) return list.filter(isStoreReviewElement);
    if (config.reviewMode !== 'product') return list;

    const scoped = list.map((el) => ({ el, match: isReviewForProduct(el, config) }));
    if (scoped.some((item) => item.match === true || item.match === false)) {
      return scoped.filter((item) => item.match === true).map((item) => item.el);
    }
    return list;
  }

  function isStoreReviewElement(el) {
    const productLink = el.querySelector(
      '.jdgm-rev__prod-link, .jdgm-rev__product-link, a.jdgm-rev__prod-link'
    );
    if (!productLink) return true;
    const href = (productLink.getAttribute('href') || '').trim();
    if (!href || href === '#' || href.startsWith('javascript:')) return true;
    // Judge.me shop reviews often link to "/" with the store name, not a product URL.
    if (href === '/' || !href.includes('/products/')) return true;
    return false;
  }

  function widgetHtmlFromResponse(data) {
    if (!data || typeof data !== 'object') return '';
    if (typeof data.widget === 'string' && data.widget.trim()) return data.widget;
    if (typeof data.all_reviews === 'string' && data.all_reviews.trim()) {
      return data.all_reviews;
    }
    return '';
  }

  function extractReviewsFromWidgetHtml(html, config, storeReviewsOnly) {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const elements = temp.querySelectorAll('.jdgm-rev');

    return Array.from(filterReviewElements(elements, config, storeReviewsOnly))
      .map((el) => {
        const rating = parseInt(
          el.querySelector('.jdgm-rev__rating')?.getAttribute('data-score'),
          10
        );
        const body =
          el.querySelector('.jdgm-rev__body')?.textContent?.trim() ||
          el.textContent?.trim() ||
          '';

        return {
          rating: Number.isFinite(rating) ? rating : 0,
          body,
          title: el.querySelector('.jdgm-rev__title')?.textContent?.trim() || '',
          name:
            el.querySelector('.jdgm-rev__author')?.textContent?.trim() ||
            'Anonymous',
          createdAt: parseReviewTimestamp(el),
        };
      })
      .filter((review) => meetsMinRating(review.rating, config.minRating));
  }

  function parseReviewTimestamp(el) {
    const timeEl = el.querySelector('time[datetime], .jdgm-rev__timestamp, [data-content]');
    const raw =
      timeEl?.getAttribute('datetime') ||
      timeEl?.getAttribute('data-content') ||
      el.getAttribute('data-created-at') ||
      '';
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function newestFirst(reviews) {
    return reviews.slice().sort((a, b) => {
      if (a.createdAt !== b.createdAt) return (b.createdAt || 0) - (a.createdAt || 0);
      return 0;
    });
  }

  async function fetchWidgetHtml(pathWithQuery, token) {
    const separator = pathWithQuery.includes('?') ? '&' : '?';
    const url = `${WIDGET_API_BASE}${pathWithQuery}${separator}api_token=${encodeURIComponent(token)}`;

    const response = await fetch(url, { method: 'GET', credentials: 'omit' });

    if (!response.ok) {
      throw new Error(`Judge.me widget request failed (${response.status})`);
    }

    const data = await response.json();
    return widgetHtmlFromResponse(data);
  }

  async function fetchStoreReviewsFromApi(config) {
    if (!config.apiTokens.length || !config.shopDomain) {
      return [];
    }

    const collected = [];
    const maxPages = 5;

    for (const token of config.apiTokens) {
      collected.length = 0;

      for (let page = 1; page <= maxPages; page += 1) {
        const params = new URLSearchParams({
          shop_domain: config.shopDomain,
          review_type: 'shop-reviews',
          page: String(page),
          sort_by: 'most-recent',
        });

        const html = await fetchWidgetHtml(
          `/widgets/all_reviews_page?${params.toString()}`,
          token
        );
        const pageReviews = extractReviewsFromWidgetHtml(html, config, false);

        if (pageReviews.length === 0) break;

        collected.push(...pageReviews);

        if (collected.length >= config.reviewsLimit) break;
      }

      if (collected.length > 0) {
        return newestFirst(collected).slice(0, config.reviewsLimit);
      }
    }

    return [];
  }

  function fetchReviewsFromMetafield(sectionEl, config) {
    const source = sectionEl.querySelector('[data-showroom-judgeme-metafield-source]');
    if (!source) return [];

    const html = (source.textContent || source.innerHTML || '').trim();
    if (!html) return [];

    const storeOnly = config.reviewMode !== 'product';
    return newestFirst(extractReviewsFromWidgetHtml(html, config, storeOnly)).slice(
      0,
      config.reviewsLimit
    );
  }

  async function fetchProductReviewsFromApi(config) {
    if (!config.apiTokens.length || !config.shopDomain || !config.productId) {
      return [];
    }

    const collected = [];
    const maxPages = 5;

    for (const token of config.apiTokens) {
      collected.length = 0;

      for (let page = 1; page <= maxPages; page += 1) {
        const params = new URLSearchParams({
          shop_domain: config.shopDomain,
          product_id: String(config.productId),
          page: String(page),
          sort_by: 'most-recent',
        });

        let html = '';
        try {
          html = await fetchWidgetHtml(`/widgets/product_review?${params.toString()}`, token);
        } catch {
          html = '';
        }

        const pageReviews = extractReviewsFromWidgetHtml(html, config, false);
        if (pageReviews.length === 0) break;

        collected.push(...pageReviews);
        if (collected.length >= config.reviewsLimit) break;
      }

      if (collected.length > 0) {
        return newestFirst(collected).slice(0, config.reviewsLimit);
      }
    }

    return [];
  }

  async function fetchReviews(sectionEl, config) {
    try {
      const fromApi =
        config.reviewMode === 'product'
          ? await fetchProductReviewsFromApi(config)
          : await fetchStoreReviewsFromApi(config);
      if (fromApi.length > 0) return fromApi;
    } catch (error) {
      console.warn('Showroom Judge.me testimonials API:', error);
    }

    return fetchReviewsFromMetafield(sectionEl, config);
  }

  function buildSlide(review, config) {
    const li = document.createElement('li');
    li.className = `splide__slide w-4/5 ${config.cardShadowClass} showroom-testimonials__slide`;

    const name = review.name || 'Anonymous';
    const company = review.title ? String(review.title).trim() : '';
    const body = review.body ? String(review.body).trim() : '';

    const cardOuterClass = config.displayOnCard
      ? 'text-scheme-card-text card showroom-testimonials__card'
      : 'showroom-testimonials__card';
    const surfaceClass = config.displayOnCard
      ? 'bg-scheme-card card__surface p-5 h-full text-center showroom-testimonials__surface'
      : 'text-center showroom-testimonials__surface';

    li.innerHTML = `
      <div class="${cardOuterClass}">
        <div class="${surfaceClass}">
          <blockquote class="text-center showroom-testimonials__quote-block">
            <span class="mb-5 block text-center tracking-rating showroom-testimonials__rating" aria-label="${escapeText(review.rating)} out of 5 stars">${starsForRating(review.rating)}</span>
            <div class="inline-rte font-body text-base showroom-testimonials__quote"><p>&#8220;${escapeText(body)}&#8221;</p></div>
            <footer class="font-heading mt-5 break-words showroom-testimonials__source">
              <strong>${escapeText(name)}</strong>
              ${company ? `<small>${escapeText(company)}</small>` : ''}
            </footer>
          </blockquote>
        </div>
      </div>
    `;

    return li;
  }

  function updateSlideshowOptions(root, reviewCount) {
    const optionsEl = root.querySelector('.slideshow-options');
    if (!optionsEl) return;

    try {
      const options = JSON.parse(optionsEl.textContent);
      options.clones = reviewCount > 1 ? reviewCount * 2 : 0;
      optionsEl.textContent = JSON.stringify(options);
    } catch {
      /* keep defaults */
    }
  }

  function mountCarousel(root, reviewCount) {
    if (reviewCount <= 1) {
      const arrows = root.querySelector('[data-showroom-judgeme-arrows]');
      if (arrows) arrows.hidden = true;
    }

    if (root.id && window.slideshows?.[root.id] && typeof window.destroySlideshow === 'function') {
      window.destroySlideshow(root);
    }

    if (typeof window.makeSlideshow === 'function') {
      window.makeSlideshow(root);
    } else if (typeof window.discoverNewSlideshows === 'function') {
      window.discoverNewSlideshows(root);
    }
  }

  function showFallback(sectionEl) {
    const fallback = sectionEl.querySelector('[data-showroom-judgeme-fallback]');
    const root = sectionEl.querySelector('[data-showroom-judgeme-root]');
    if (root) root.hidden = true;
    if (!fallback) return;
    fallback.hidden = false;
    const splide = fallback.querySelector('.splide');
    if (splide?.id && typeof window.makeSlideshow === 'function') {
      window.makeSlideshow(splide);
    }
  }

  function updateProductSummary(sectionEl, reviews, config) {
    if (config.reviewMode !== 'product') return;

    const title =
      sectionEl.querySelector('[data-showroom-judgeme-title]') ||
      sectionEl.querySelector('.showroom-testimonials__title');
    const stars = sectionEl.querySelector('[data-showroom-judgeme-stars]');

    if (!reviews.length) {
      if (stars) {
        stars.hidden = false;
        stars.textContent = '★★★★★';
        stars.setAttribute('aria-label', '5 out of 5 stars');
      }
      if (title) title.textContent = config.emptyMessage;
      return;
    }

    const avg =
      reviews.reduce((sum, review) => sum + (Number(review.rating) || 0), 0) / reviews.length;
    const rounded = Math.max(1, Math.min(5, Math.round(avg)));
    if (stars) {
      stars.hidden = false;
      stars.textContent = '★'.repeat(rounded);
      stars.setAttribute('aria-label', `${avg.toFixed(1)} out of 5 stars`);
    }
    if (title) {
      const suffix = config.summarySuffix || 'reviews';
      title.textContent = `Rated ${avg.toFixed(1)} by ${reviews.length}+ ${suffix}`;
    }
  }

  function showProductEmpty(sectionEl, config) {
    const root = sectionEl.querySelector('[data-showroom-judgeme-root]');
    const fallback = sectionEl.querySelector('[data-showroom-judgeme-fallback]');
    if (root) root.hidden = true;
    if (fallback) fallback.hidden = true;
    updateProductSummary(sectionEl, [], config);
  }

  function resetSection(sectionEl) {
    const root = sectionEl.querySelector('[data-showroom-judgeme-root]');
    if (!root) return;
    if (root.id && typeof window.destroySlideshow === 'function') {
      window.destroySlideshow(root);
    }
    delete root.dataset.showroomJudgemeLoaded;
    root.hidden = false;
    const fallback = sectionEl.querySelector('[data-showroom-judgeme-fallback]');
    if (fallback) fallback.hidden = true;
  }

  function bindWriteReview(sectionEl) {
    const buttons = sectionEl.querySelectorAll('[data-showroom-judgeme-write]');
    if (!buttons.length) return;
    const sectionConfig = parseConfig(sectionEl) || {};

    const jq = () => window.jdgm && (window.jdgm.$ || window.jQuery);

    const loadJudgeMeFormAssets = () => {
      if (!window.jdgm) return;
      ensureJudgeMeStarHelpers();
      const host = window.jdgm.CDN_HOST || 'https://cdnwidget.judge.me/';
      if (typeof window.jdgm.loadScript === 'function') {
        window.jdgm.loadScript(`${host}widget/form.js`);
        window.jdgm.loadScript(`${host}widget/write_review_modal.js`);
      }
      if (typeof window.jdgm.loadCSS === 'function' && typeof window.jdgm.widgetPath === 'function') {
        window.jdgm.loadCSS(window.jdgm.widgetPath('base.css'));
        window.jdgm.loadCSS(window.jdgm.widgetPath('form.css'));
      }
    };

    const waitForModalClass = () =>
      new Promise((resolve, reject) => {
        const started = Date.now();
        const poll = () => {
          if (window.jdgm && typeof window.jdgm._WriteReviewModal === 'function' && jq()) {
            resolve(window.jdgm._WriteReviewModal);
            return;
          }
          if (Date.now() - started > 4000) {
            reject(new Error('Judge.me write-review modal did not load'));
            return;
          }
          window.setTimeout(poll, 50);
        };
        poll();
      });

    const patchStoreReviewSubmit = (modal) => {
      if (modal.__showroomSubmitPatched) return modal;
      modal.__showroomSubmitPatched = true;
      const buildPayload = modal._buildReviewPayload.bind(modal);
      modal._buildReviewPayload = function () {
        if (this.isLeavingShopReview && typeof this._buildStoreReviewSnapshot === 'function') {
          this.storeReviewData = this._buildStoreReviewSnapshot();
        }
        const payload = buildPayload();
        if (this.isLeavingShopReview) {
          payload.id = undefined;
          payload.judgeme_review_source =
            payload.judgeme_review_source || 'store-review-collection';
        }
        return payload;
      };
      return modal;
    };

    const hydrateProductWidget = (config) => {
      const widget =
        document.querySelector('#showroom_judgeme_review_widget.jdgm-review-widget') ||
        document.querySelector('.showroom-testimonials__jdgm-widget.jdgm-review-widget');
      if (!widget || !config.productId) return;

      widget.setAttribute('data-id', String(config.productId));
      if (config.productTitle) widget.setAttribute('data-product-title', config.productTitle);
      if (!config.productImage) return;

      widget.setAttribute('data-product-image-url', config.productImage);
      let inner = widget.querySelector('.jdgm-rev-widg, .jm-review-widget');
      if (!inner) {
        inner = document.createElement('div');
        inner.className = 'jdgm-rev-widg';
        widget.appendChild(inner);
      }
      inner.setAttribute('data-image-url', config.productImage);
    };

    const ensureProductReviewModal = (config) => {
      const productId = config.productId;
      const key = `__showroomJdgmProductModalPromise_${productId}`;
      if (window[key]) return window[key];

      window[key] = waitForModalClass().then((Modal) => {
        hydrateProductWidget(config);
        const modal = new Modal(jq());
        modal.isLeavingShopReview = false;
        return Promise.resolve(modal.setup('jdgm-review-widget-modal', productId, {})).then(
          (ready) => {
            if (!ready) throw new Error('Judge.me modal setup failed');
            modal.isLeavingShopReview = false;
            return modal;
          }
        );
      });

      window[key].catch(() => {
        window[key] = null;
      });

      return window[key];
    };

    const ensureStoreReviewModal = () => {
      if (window.__showroomJdgmStoreModalPromise) return window.__showroomJdgmStoreModalPromise;

      window.__showroomJdgmStoreModalPromise = waitForModalClass().then((Modal) => {
        const modal = new Modal(jq());
        modal.isLeavingShopReview = true;
        modal.startedStoreReviewFromIntro = true;
        return Promise.resolve(
          modal.setup('jdgm-review-widget-modal', 1, {
            isLeavingShopReview: true,
            startedStoreReviewFromIntro: true,
          })
        ).then((ready) => {
          if (!ready) throw new Error('Judge.me modal setup failed');
          modal.isLeavingShopReview = true;
          modal.startedStoreReviewFromIntro = true;
          return patchStoreReviewSubmit(modal);
        });
      });

      window.__showroomJdgmStoreModalPromise.catch(() => {
        window.__showroomJdgmStoreModalPromise = null;
      });

      return window.__showroomJdgmStoreModalPromise;
    };

    const openHostedForm = (shopDomain, productId) => {
      const domain = String(shopDomain || '').trim();
      if (!domain) return false;
      const id = parseInt(productId, 10) || 0;
      window.open(
        `https://api.judge.me/storefront_reviews/new?shop_domain=${encodeURIComponent(
          domain
        )}&platform=shopify&product_id=${encodeURIComponent(id)}`,
        '_blank',
        'noopener'
      );
      return true;
    };

    const readVisibleRating = () => {
      const page = document.querySelector('.jdgm-write-review-modal__page--review-details');
      if (!page) return 0;
      const checked = page.querySelector('.jdgm-star[aria-checked="true"]');
      if (checked) {
        return parseInt(checked.getAttribute('data-alt') || checked.getAttribute('title'), 10) || 0;
      }
      const selected = page.querySelectorAll('.jdgm-star.jdgm--on');
      if (selected.length) {
        const last = selected[selected.length - 1];
        return (
          parseInt(last.getAttribute('data-alt') || last.getAttribute('title') || String(selected.length), 10) ||
          selected.length
        );
      }
      const scoreInput = page.querySelector('input[name="score"], input[type="hidden"]');
      return scoreInput ? parseInt(scoreInput.value, 10) || 0 : 0;
    };

    const ensureJudgeMeStarHelpers = () => {
      window.jdgm = window.jdgm || {};
      if (typeof window.jdgm._rtlStarsEnabled !== 'function') {
        window.jdgm._rtlStarsEnabled = function () {
          const settings = window.jdgmSettings || {};
          return !!settings['rtl_stars_enabled_' + settings.shop_locale];
        };
      }
      if (typeof window.jdgm._applyRtlStarsOrder !== 'function') {
        window.jdgm._applyRtlStarsOrder = function () {};
      }
    };

    const applyStoreReviewCopy = (modal) => {
      if (modal) {
        modal.isLeavingShopReview = true;
        modal.startedStoreReviewFromIntro = true;
      }

      const settings = window.jdgmSettings || {};
      const title =
        settings.store_review_form_title_text || 'How would you rate this store?';
      const intro =
        settings.store_review_form_introduction_text ||
        'We would love it if you would share a bit about your experience.';

      document
        .querySelectorAll('.jdgm-write-review-modal__page--review-form-intro')
        .forEach((page) => {
          const heading = page.querySelector('.jdgm-write-review-modal__title');
          if (heading) heading.textContent = title;
          const lead = page.querySelector('.jdgm-write-review-modal__intro');
          if (lead) lead.textContent = intro;
          if (!page.querySelector('.jdgm-write-review-modal__product-title')) {
            const stars = page.querySelector('.jdgm-write-review-modal__stars');
            const name = document.createElement('div');
            name.className = 'jdgm-write-review-modal__product-title';
            name.setAttribute('role', 'heading');
            name.setAttribute('aria-level', '3');
            name.textContent = settings.shop_name || 'Showroom Coffee';
            if (stars && stars.parentNode) {
              stars.parentNode.insertBefore(name, stars);
            }
          }
        });
    };

    const prepareOpenModal = (modal, isStoreReview) => {
      window.__showroomJdgmStoreModalInstance = isStoreReview ? modal : null;
      const root = document.querySelector('.jdgm-write-review-modal');
      if (!root) return;
      root.querySelectorAll('button').forEach((btn) => {
        if (!btn.getAttribute('type')) btn.setAttribute('type', 'button');
      });
      if (isStoreReview) applyStoreReviewCopy(modal);
    };

    if (!window.__showroomJdgmNextPatch) {
      window.__showroomJdgmNextPatch = true;
      document.addEventListener(
        'click',
        (event) => {
          const next = event.target.closest
            ? event.target.closest('.jdgm-write-review-modal__nav-btn-next')
            : null;
          if (!next) return;
          const modal = window.__showroomJdgmStoreModalInstance;
          if (!modal) return;
          const rating = readVisibleRating();
          if (!(modal.rating >= 1 && modal.rating <= 5) && rating >= 1 && rating <= 5) {
            modal.rating = rating;
          }
        },
        true
      );
    }

    const openForm = (event) => {
      event.preventDefault();
      const button = event.currentTarget;
      const config = parseConfig(sectionEl) || sectionConfig;
      const isProduct = config.reviewMode === 'product' && config.productId > 0;
      loadJudgeMeFormAssets();

      const ready = isProduct
        ? ensureProductReviewModal(config)
        : ensureStoreReviewModal();

      ready
        .then((modal) => {
          ensureJudgeMeStarHelpers();
          modal.isLeavingShopReview = !isProduct;
          modal.startedStoreReviewFromIntro = !isProduct;
          if (
            !isProduct &&
            typeof modal._remountIntroPage === 'function' &&
            !modal.__showroomSkipRemount
          ) {
            modal.__showroomSkipRemount = modal._remountIntroPage;
            modal._remountIntroPage = function () {
              return this;
            };
          }
          try {
            modal.showModalPage(1);
          } catch (error) {
            console.warn('Showroom Judge.me modal open:', error);
          }
          prepareOpenModal(modal, !isProduct);
        })
        .catch(() => {
          openHostedForm(
            button.getAttribute('data-shop-domain'),
            isProduct ? config.productId : 0
          );
        });
    };

    buttons.forEach((button) => {
      if (button.dataset.showroomJudgemeWriteBound === 'true') return;
      button.dataset.showroomJudgemeWriteBound = 'true';
      button.addEventListener('click', openForm);
    });
  }

  async function initSection(sectionEl) {
    bindWriteReview(sectionEl);
    const root = sectionEl.querySelector('[data-showroom-judgeme-root]');
    if (!root || root.dataset.showroomJudgemeLoaded === 'true') return;

    const config = parseConfig(sectionEl);
    if (!config) return;

    const listEl = root.querySelector('[data-showroom-judgeme-list]');
    if (!listEl) return;

    const canLoad =
      config.apiTokens.length > 0 ||
      sectionEl.querySelector('[data-showroom-judgeme-metafield-source]');
    if (!canLoad) return;

    root.dataset.showroomJudgemeLoaded = 'loading';

    try {
      const reviews = await fetchReviews(sectionEl, config);

      listEl.innerHTML = '';
      if (reviews.length === 0) {
        if (config.reviewMode === 'product') {
          showProductEmpty(sectionEl, config);
        } else {
          showFallback(sectionEl);
        }
        root.dataset.showroomJudgemeLoaded = 'empty';
        return;
      }

      updateProductSummary(sectionEl, reviews, config);

      reviews.forEach((review) => {
        listEl.appendChild(buildSlide(review, config));
      });

      updateSlideshowOptions(root, reviews.length);
      mountCarousel(root, reviews.length);
      root.dataset.showroomJudgemeLoaded = 'true';
      sectionEl.classList.toggle('showroom-testimonials--few-reviews', reviews.length < 3);
    } catch (error) {
      console.error('Showroom Judge.me testimonials:', error);
      if (config.reviewMode === 'product') {
        showProductEmpty(sectionEl, config);
      } else {
        showFallback(sectionEl);
      }
      root.dataset.showroomJudgemeLoaded = 'error';
    }
  }

  function initAll(container) {
    container.querySelectorAll('[data-showroom-judgeme-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    event.target.querySelectorAll('[data-showroom-judgeme-section]').forEach(resetSection);
    initAll(event.target);
  });
})();
